import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, Circle } from 'react-leaflet';
import { Icon, divIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ClusterLayer from './ClusterLayer';
import LocationButton from './LocationButton';
import HelpButton from './HelpButton';
// Plain JS version (no TypeScript types)

const aedIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const aedOsmIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function MapUpdater({ center, zoom, userLocation }) {
  const map = useMap();
  const prevCenterRef = useRef(center);
  const prevZoomRef = useRef(zoom);
  
  useEffect(() => {
    const centerChanged = JSON.stringify(prevCenterRef.current) !== JSON.stringify(center);
    const zoomChanged = prevZoomRef.current !== zoom;
    
    if (centerChanged || zoomChanged) {
      map.setView(center, zoom);
      prevCenterRef.current = center;
      prevZoomRef.current = zoom;
    }
  }, [center, zoom, map]);
  
  return null;
}

function UserLocation() {
  const [position, setPosition] = useState(null);
  const map = useMap();
  const watchId = useRef(null);
  const circleRef = useRef(null);

  // Effet pour démarrer et arrêter la surveillance de la position
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }

    // Options pour la géolocalisation
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    // Fonction de succès de la géolocalisation
    const success = (pos) => {
      const { latitude, longitude, accuracy } = pos.coords;
      const newPosition = [latitude, longitude];
      
      // Mise à jour de la position
      setPosition(newPosition);
      
      // Si c'est la première position, centrer la carte dessus
      if (!circleRef.current) {
        map.setView(newPosition, 18);
      }
    };

    // Fonction d'erreur de la géolocalisation
    const error = (err) => {
      console.warn(`ERREUR (${err.code}): ${err.message}`);
    };

    // Démarrer la surveillance de la position
    watchId.current = navigator.geolocation.watchPosition(success, error, options);

    // Nettoyage à la désactivation du composant
    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, [map]);

  // Si la position n'est pas encore disponible, ne rien afficher
  if (!position) return null;

  return (
    <Circle
      center={position}
      pathOptions={{ color: 'blue', fillColor: '#3b82f6', fillOpacity: 0.5 }}
      radius={10}
      ref={circleRef}
    >
      <Popup>Votre position</Popup>
    </Circle>
  );
}

function OSMCachedLayer({ osmNodes, onVisibleOsmChange }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const [bounds, setBounds] = useState(map.getBounds());
  const [tick, setTick] = useState(0);

  // Update bounds and zoom when map moves
  useEffect(() => {
    const update = () => {
      setBounds(map.getBounds());
      setZoom(map.getZoom());
      setTick(t => t + 1);
    };
    
    map.on('moveend', update);
    map.on('zoomend', update);
    update(); // Initial update
    
    return () => {
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map]);

  // Prepare OSM nodes as points
  const nodes = Array.isArray(osmNodes) ? osmNodes : [];
  const points = nodes.map(node => ({
    id: `osm-${node.id}`,
    latitude: node.lat,
    longitude: node.lon,
    name: node.tags?.name || 'OSM AED',
    operator: node.tags?.operator,
    source: 'OpenStreetMap'
  }));

  // Filter points to current viewport
  const visiblePoints = points.filter(node => 
    node.latitude >= bounds.getSouth() && 
    node.latitude <= bounds.getNorth() &&
    node.longitude >= bounds.getWest() && 
    node.longitude <= bounds.getEast()
  );

  // Update visible count
  useEffect(() => {
    if (typeof onVisibleOsmChange === 'function') {
      onVisibleOsmChange(visiblePoints.length);
    }
  }, [onVisibleOsmChange, visiblePoints.length]);

  const handleClusterClick = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    map.setView([latitude, longitude], map.getZoom() + 2, {
      animate: true,
    });
  };

  return (
    <ClusterLayer
      points={points}
      zoom={zoom}
      bounds={bounds}
      onClusterClick={handleClusterClick}
      icon={aedOsmIcon}
    />
  );
}

function LocalAedsLayer({ aeds, onVisibleLocalChange }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  const [bounds, setBounds] = useState(map.getBounds());
  const [tick, setTick] = useState(0);

  // Update bounds and zoom when map moves
  useEffect(() => {
    const update = () => {
      setBounds(map.getBounds());
      setZoom(map.getZoom());
      setTick(t => t + 1);
    };
    
    map.on('moveend', update);
    map.on('zoomend', update);
    update(); // Initial update
    
    return () => {
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map]);

  // Filter and prepare points
  const points = (aeds || [])
    .filter(aed => 
      aed && 
      typeof aed.latitude === 'number' && 
      typeof aed.longitude === 'number'
    )
    .map(aed => ({
      id: aed.id,
      latitude: aed.latitude,
      longitude: aed.longitude,
      name: aed.name,
      address: aed.address,
      accessibility: aed.accessibility
    }));

  // Update visible count
  useEffect(() => {
    if (typeof onVisibleLocalChange === 'function') {
      onVisibleLocalChange(points.length);
    }
  }, [onVisibleLocalChange, points.length]);

  const handleClusterClick = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    map.setView([latitude, longitude], map.getZoom() + 2, {
      animate: true,
    });
  };

  return (
    <ClusterLayer
      points={points}
      zoom={zoom}
      bounds={bounds}
      onClusterClick={handleClusterClick}
      icon={aedIcon}
    />
  );
}

function MapEventListener({ onMapMove }) {
  useMapEvents({
    moveend: (e) => {
      const center = e.target.getCenter();
      if (onMapMove) {
        onMapMove(center);
      }
    },
  });
  return null;
}

export function AEDMap({ aeds, center, zoom, onVisibleOsmChange, onVisibleLocalChange, osmNodes, onMapMove }) {
  const [userLocation, setUserLocation] = useState(center);
  
  // Update user location if it changes
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation([pos.coords.latitude, pos.coords.longitude]);
        },
        (err) => {
          console.warn(`ERROR (${err.code}): ${err.message}`);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    }
  }, []);

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      className="w-full h-full"
      style={{ minHeight: '500px' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapEventListener onMapMove={onMapMove} />
      <MapUpdater center={center} zoom={zoom} userLocation={userLocation} />
      <OSMCachedLayer osmNodes={osmNodes} onVisibleOsmChange={onVisibleOsmChange} />
      <LocalAedsLayer aeds={aeds} onVisibleLocalChange={onVisibleLocalChange} />
      <UserLocation />
      <LocationButton position={userLocation} zoom={20} />
      <HelpButton />
    </MapContainer>
  );
}

